import { useCallback, useMemo, useState } from "react";
import { Flex, Button, Text, TextField, Box } from "@radix-ui/themes";
import { useMutation } from "react-query";
import { toast } from "sonner";

import { Dialog } from "@/components/shared/dialog";
import {
  MultiSelectWithOverflow,
  SelectOption,
} from "./multi-select-with-overflow";
import { useRegressionApi } from "@/v2/entities/regression/Regression.api";
import { useSimulator } from "@/v2/features/simulator/context/SimulatorWrapper";
import { useGetTopics } from "@/api/useGetTopics";
import { Topic } from "@/v2/entities/topic/Topic.model";

interface CreateTestSetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function CreateTestSetDialog({
  open,
  onOpenChange,
  onSuccess,
}: CreateTestSetDialogProps) {
  const regressionApi = useRegressionApi();
  const { simulator, releasesWithVersions } = useSimulator();
  const { data: topics }: { data: Topic[] | undefined } = useGetTopics();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedTopicIds, setSelectedTopicIds] = useState<string[]>([]);
  const [selectedReleaseIds, setSelectedReleaseIds] = useState<string[]>([]);

  const topicOptions: SelectOption[] = useMemo(() => {
    if (!topics) return [];
    return topics.map((topic) => ({
      label: topic.topic,
      value: topic.id.toString(),
    }));
  }, [topics]);

  const releaseOptions: SelectOption[] = useMemo(() => {
    if (!releasesWithVersions) return [];
    return releasesWithVersions.map((release) => ({
      label: `v${release.version}`,
      value: release.id.toString(),
    }));
  }, [releasesWithVersions]);

  const { mutate: createTestSet, isLoading } = useMutation(
    () =>
      regressionApi.createRegressionSetFromConversations({
        name,
        description: description.trim() || undefined,
        topicIds:
          selectedTopicIds.length > 0
            ? selectedTopicIds.map((id) => parseInt(id, 10))
            : undefined,
        releaseIds:
          selectedReleaseIds.length > 0
            ? selectedReleaseIds.map((id) => parseInt(id, 10))
            : undefined,
        simulatorId: simulator?.id,
      }),
    {
      onSuccess: (data) => {
        toast.success(
          `Created test set "${data.name}" with ${data.items.length} questions`
        );
        resetForm();
        onSuccess();
      },
      onError: () => {
        toast.error("Failed to create test set");
      },
    }
  );

  const resetForm = useCallback(() => {
    setName("");
    setDescription("");
    setSelectedTopicIds([]);
    setSelectedReleaseIds([]);
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onOpenChange(false);
  }, [resetForm, onOpenChange]);

  const handleCreate = useCallback(() => {
    if (!name.trim()) {
      toast.error("Please enter a name for the test set");
      return;
    }
    createTestSet();
  }, [name, createTestSet]);

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setName(e.target.value);
    },
    []
  );

  const handleDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setDescription(e.target.value);
    },
    []
  );

  const handleTopicChange = useCallback((values: string[]) => {
    setSelectedTopicIds(values);
  }, []);

  const handleReleaseChange = useCallback((values: string[]) => {
    setSelectedReleaseIds(values);
  }, []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content size="medium">
        <Flex direction="column" gap="4" p="4">
          <Text size="5" weight="medium">
            Create Test Set
          </Text>

          <Text size="2" color="gray">
            Create a regression test from training sessions with good feedback.
            Up to 200 most recent conversations will be selected.
          </Text>

          <Box>
            <Text size="2" weight="regular" mb="1" as="label">
              Name
            </Text>
            <TextField.Root
              placeholder="Enter test set name"
              value={name}
              onChange={handleNameChange}
            />
          </Box>

          <Box>
            <Text size="2" weight="regular" mb="1" as="label">
              Description (optional)
            </Text>
            <TextField.Root
              placeholder="Enter description"
              value={description}
              onChange={handleDescriptionChange}
            />
          </Box>

          <Box>
            <Text size="2" weight="regular" mb="1" as="label">
              Filter by Topics (optional)
            </Text>
            <MultiSelectWithOverflow
              options={topicOptions}
              value={selectedTopicIds}
              onChange={handleTopicChange}
              placeholder="All topics"
              maxVisibleItems={5}
            />
          </Box>

          <Box>
            <Text size="2" weight="regular" mb="1" as="label">
              Filter by Versions (optional)
            </Text>
            <MultiSelectWithOverflow
              options={releaseOptions}
              value={selectedReleaseIds}
              onChange={handleReleaseChange}
              placeholder="All versions"
              maxVisibleItems={5}
            />
          </Box>

          <Flex gap="3" mt="2" justify="end">
            <Button variant="soft" color="gray" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              color="gray"
              highContrast
              onClick={handleCreate}
              disabled={isLoading || !name.trim()}
            >
              {isLoading ? "Creating..." : "Create Test Set"}
            </Button>
          </Flex>
        </Flex>
      </Dialog.Content>
    </Dialog>
  );
}
